/**
 * AetherLite — root app.
 *
 * Composición a TRES niveles:
 *
 *   1. Path dispatcher (App)  — branch según `window.location.pathname`:
 *        - "/p/:short_code" → <PublicPlanApp> (sin auth, sin shell, sin catalog)
 *        - "/" sin auth     → <LandingApp>    (sin shell, con login Google embed)
 *        - cualquier otro   → <MainApp>       (shell autenticado completo)
 *
 *   2. Hash router (dentro de MainApp/Root)  — `#/dashboard`, `#/templates`, …
 *
 *   3. Section render (dentro de PlanBuilder)  — secciones del plan.
 *
 *   TODO Fase 8 — nginx rewrite:
 *      Para que `/p/:short_code` funcione en producción al refrescar la página
 *      o entrar directo, el vhost de Hostinger debe mandar todas las paths
 *      desconocidas a `/index.html` (SPA fallback). En la config de nginx:
 *
 *        location / { try_files $uri $uri/ /index.html; }
 *
 *      Sin esa línea, abrir `aetherlite.nilabs.co/p/abc123` directamente
 *      devuelve 404 del nginx en vez de cargar el SPA.
 */
import { useCallback, useEffect, useState, type CSSProperties } from "react";
import { Icon } from "./lib/Icon";
import { Avatar } from "./lib/components";
import {
  TweakColor,
  TweakRadio,
  TweakSection,
  TweakSelect,
  TweaksPanel,
  useTweaks,
} from "./lib/TweaksPanel";
import { ToastProvider } from "./lib/toast";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { CatalogProvider } from "./contexts/CatalogContext";
import { LoginScreen } from "./screens/LoginScreen";
import { PlaceholderScreen } from "./screens/PlaceholderScreen";
import { PlanBuilderScreen } from "./screens/PlanBuilderScreen";
import { DashboardScreen } from "./screens/DashboardScreen";
import { TemplatesScreen } from "./screens/TemplatesScreen";
import { ClientsScreen } from "./screens/ClientsScreen";
import { AgendaScreen } from "./screens/AgendaScreen";
import { LibraryScreen } from "./screens/LibraryScreen";
import { MeasurementsScreen } from "./screens/MeasurementsScreen";
import { BrandScreen } from "./screens/BrandScreen";
import { ConfigScreen } from "./screens/ConfigScreen";
import { AdminDashboardScreen } from "./screens/admin/AdminDashboardScreen";
import { AdminUsersScreen } from "./screens/admin/AdminUsersScreen";
import { AdminPaymentsScreen } from "./screens/admin/AdminPaymentsScreen";
import { AdminCouponsScreen } from "./screens/admin/AdminCouponsScreen";
import { AdminReportsScreen } from "./screens/admin/AdminReportsScreen";
import { AdminFoodsScreen } from "./screens/admin/AdminFoodsScreen";
import { AdminExercisesScreen } from "./screens/admin/AdminExercisesScreen";
import { AdminAdminsScreen } from "./screens/admin/AdminAdminsScreen";
import { AdminAuditLogScreen } from "./screens/admin/AdminAuditLogScreen";
import { AdminAssetsScreen } from "./screens/admin/AdminAssetsScreen";
import { AdminAiUsageScreen } from "./screens/admin/AdminAiUsageScreen";
import { PublicPlanScreen } from "./screens/PublicPlanScreen";
import { ExerciseDetailScreen } from "./screens/ExerciseDetailScreen";
import { PaymentReturnScreen } from "./screens/PaymentReturnScreen";
import { LandingScreen } from "./screens/LandingScreen";
import { TermsScreen } from "./screens/TermsScreen";
import { PrivacyScreen } from "./screens/PrivacyScreen";
import { DeletionScreen } from "./screens/DeletionScreen";
import { DEFAULT_TWEAKS, ROUTE_TITLES, type Route, type Tweaks } from "./types";

// ----- Hash routing helpers -----

const VALID_ROUTES: Route[] = [
  "dashboard", "templates", "clients", "agenda", "library", "measurements", "brand", "config",
  "plan-builder", "share-link",
  "admin-dashboard", "admin-users", "admin-payments", "admin-coupons", "admin-reports", "admin-foods", "admin-exercises",
  "admin-admins", "admin-audit-log", "admin-assets", "admin-ai-usage",
  "public-plan", "login", "terms", "privacy",
];

function parseHashRoute(): Route {
  const raw = (typeof window !== "undefined" ? window.location.hash : "").replace(/^#\/?/, "");
  const candidate = raw.split("?")[0].split("/")[0] as Route;
  return VALID_ROUTES.includes(candidate) ? candidate : "dashboard";
}

function setHashRoute(r: Route, query?: string) {
  window.location.hash = query ? `#/${r}?${query}` : `#/${r}`;
}

// ----- App tree -----

/**
 * Path dispatcher — antes que cualquier provider pesado.
 *
 * Estrategia:
 *   - `/p/:short_code` → vista pública del plan, sin auth ni catálogo. Es la
 *     ruta que los clientes finales del entrenador abren desde su móvil.
 *     Mantenemos el bundle minimal: ToastProvider + PublicPlanScreen.
 *   - cualquier otro path → MainApp con providers completos (auth, catalog,
 *     tweaks). MainApp luego decide internamente landing vs login vs shell
 *     según el estado de auth y la ruta hash.
 *
 * Importante: este dispatcher se ejecuta UNA SOLA VEZ al montar. Si en el
 * futuro queremos navegación SPA cliente entre `/p/...` ↔ `/`, hay que escuchar
 * `popstate` y forzar re-render. Hoy no es necesario porque el flujo natural es:
 * el cliente recibe el link `/p/abc`, lo abre, lee su plan, cierra. Nunca
 * navega de ahí al editor sin recargar.
 */
export default function App() {
  if (typeof window !== "undefined") {
    const pathname = window.location.pathname;
    if (pathname.startsWith("/p/")) {
      const shortCode = pathname.slice(3).replace(/\/+$/, "").split("/")[0];
      return (
        <ToastProvider>
          <PublicPlanScreen shortCode={shortCode} />
        </ToastProvider>
      );
    }
    if (pathname.startsWith("/exercise/")) {
      // Página pública de detalle de un ejercicio — abierta en pestaña nueva
      // desde el link del plan.
      const exerciseId = pathname.slice("/exercise/".length).replace(/\/+$/, "").split("/")[0];
      return (
        <ToastProvider>
          <ExerciseDetailScreen exerciseId={exerciseId} />
        </ToastProvider>
      );
    }
    // URLs públicas directas (sin hash) de las páginas legales — Google Play /
    // App Store requieren una URL plana de la política de privacidad. El SPA
    // fallback de nginx sirve index.html para cualquier ruta, y acá la
    // detectamos para montar la página standalone sin login ni shell.
    if (pathname === "/privacy" || pathname === "/privacy/") {
      return (
        <ToastProvider>
          <PrivacyScreen standalone />
        </ToastProvider>
      );
    }
    if (pathname === "/terms" || pathname === "/terms/") {
      return (
        <ToastProvider>
          <TermsScreen standalone />
        </ToastProvider>
      );
    }
    if (
      pathname === "/eliminar-cuenta" || pathname === "/eliminar-cuenta/" ||
      pathname === "/account-deletion" || pathname === "/account-deletion/"
    ) {
      return (
        <ToastProvider>
          <DeletionScreen standalone />
        </ToastProvider>
      );
    }
    if (pathname.startsWith("/payments/return")) {
      // Página de retorno post-checkout MercadoPago.
      // URL format: /payments/return/{tier}/{mode}?collection_id=...&payment_id=...
      // (tier y mode vienen via path porque MP agrega sus propios query params
      //  con `?` adicional que rompería el parsing si los pusiéramos en query nosotros).
      // Requiere AuthProvider (necesita refrescar el user al activarse el plan).
      return (
        <ToastProvider>
          <AuthProvider>
            <PaymentReturnScreen />
          </AuthProvider>
        </ToastProvider>
      );
    }
  }
  return (
    <ToastProvider>
      <AuthProvider>
        <CatalogProvider>
          <Root />
        </CatalogProvider>
      </AuthProvider>
    </ToastProvider>
  );
}

function Root() {
  const { user, loading } = useAuth();
  const [tweaks, setTweak] = useTweaks<Tweaks>(DEFAULT_TWEAKS);
  // Tick para re-render cuando cambia el hash y el usuario está deslogueado
  // (alterna entre LandingScreen / TermsScreen / PrivacyScreen / LoginScreen).
  // Cuando hay usuario, AppShell maneja su propio listener de hashchange.
  const [, setHashTick] = useState(0);
  useEffect(() => {
    const onHash = () => setHashTick((t) => t + 1);
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  // Aplicar tweaks como CSS vars + data attrs (mismo patrón que Panini).
  useEffect(() => {
    document.documentElement.dataset.theme = tweaks.theme;
    document.documentElement.dataset.density = tweaks.density;
    const root = document.documentElement.style;
    root.setProperty("--primary", tweaks.primaryColor);
    root.setProperty("--accent-magenta", tweaks.accentColor);
    root.setProperty("--font-display", `'${tweaks.fontDisplay}', system-ui, sans-serif`);
    root.setProperty("--font-body", `'${tweaks.fontBody}', system-ui, sans-serif`);
  }, [tweaks]);

  if (loading) {
    return (
      <div style={{ display: "grid", placeItems: "center", height: "100vh", color: "var(--fg-muted)" }}>
        <div style={{ textAlign: "center" }}>
          <div className="brand-mark" style={{ width: 56, height: 56, fontSize: 24, fontFamily: "var(--font-display)", margin: "0 auto 16px" }}>AL</div>
          <div>Cargando…</div>
        </div>
        <TweaksPanelMounted tweaks={tweaks} setTweak={setTweak} />
      </div>
    );
  }

  if (!user) {
    // Visitante sin login — decidimos qué mostrar según la ruta hash:
    //   - terms/privacy → versión standalone (con botón "← Volver al inicio")
    //   - login         → LoginScreen explícito
    //   - cualquier otro (incluyendo "" / dashboard fallback) → LandingScreen
    //
    // No mostramos TweaksPanel para visitantes — es una herramienta interna del editor.
    const route = parseHashRoute();
    const hashRaw = (typeof window !== "undefined" ? window.location.hash : "")
      .replace(/^#\/?/, "")
      .split("?")[0]
      .split("/")[0];
    if (route === "terms") return <TermsScreen standalone />;
    if (route === "privacy") return <PrivacyScreen standalone />;
    if (route === "login" || hashRaw === "login") return <LoginScreen />;
    return <LandingScreen />;
  }

  return <AppShell tweaks={tweaks} setTweak={setTweak} />;
}

interface AppShellProps {
  tweaks: Tweaks;
  setTweak: (key: keyof Tweaks | Partial<Tweaks>, value?: Tweaks[keyof Tweaks]) => void;
}

function AppShell({ tweaks, setTweak }: AppShellProps) {
  const { user, logout } = useAuth();
  const [route, setRouteState] = useState<Route>(parseHashRoute());
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // Sync route ↔ hash. Cualquier cambio externo (back/forward, link manual)
  // se refleja en estado; cualquier `go(r)` interno actualiza el hash.
  useEffect(() => {
    const onHash = () => setRouteState(parseHashRoute());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const go = useCallback((r: Route, query?: string) => {
    setHashRoute(r, query);
    setRouteState(r);
    setShowMobileMenu(false);
  }, []);

  if (!user) return null;
  const isAdmin = Boolean(user.is_admin);

  // Modo "editor fullscreen": al entrar al PlanBuilder escondemos side-rail,
  // top-bar y bottom-nav para maximizar el área de preview. La pantalla del
  // editor trae su propia topbar con botón "Volver".
  if (route === "plan-builder") {
    return (
      <>
        <RouteDispatch route={route} go={go} />
        <TweaksPanelMounted tweaks={tweaks} setTweak={setTweak} />
      </>
    );
  }

  return (
    <>
      <div className="app-root">
        <SideRail user={user} route={route} go={go} isAdmin={isAdmin} onLogout={logout} />
        <div className="app-main">
          <TopBar route={route} go={go} user={user} onToggleMobileMenu={() => setShowMobileMenu((v) => !v)} />

          {showMobileMenu && (
            <MobileMenu route={route} go={go} isAdmin={isAdmin} onLogout={logout} />
          )}

          <RouteDispatch route={route} go={go} />

          <Footer go={go} />
        </div>
      </div>

      <BottomNav route={route} go={go} />

      <TweaksPanelMounted tweaks={tweaks} setTweak={setTweak} />
    </>
  );
}

interface RouteDispatchProps {
  route: Route;
  go: (r: Route, query?: string) => void;
}

/**
 * Mapea la ruta hash → screen real. Las pantallas implementadas en Fase 5
 * reciben `go` para navegar (con querystring opcional). Las rutas no
 * implementadas (admin-*, public-plan, terms, privacy, share-link, login)
 * caen al PlaceholderScreen — Fase 6/7.
 */
function RouteDispatch({ route, go }: RouteDispatchProps) {
  switch (route) {
    case "dashboard":
      return <DashboardScreen go={go} />;
    case "templates":
      return <TemplatesScreen go={go} />;
    case "clients":
      return <ClientsScreen go={go} />;
    case "agenda":
      return <AgendaScreen go={go} />;
    case "library":
      return <LibraryScreen />;
    case "measurements":
      return <MeasurementsScreen />;
    case "brand":
      return <BrandScreen />;
    case "config":
      return <ConfigScreen go={go} />;
    case "plan-builder":
      return <PlanBuilderScreen onBack={() => go("dashboard")} />;
    case "admin-dashboard":
      return <AdminDashboardScreen />;
    case "admin-users":
      return <AdminUsersScreen />;
    case "admin-payments":
      return <AdminPaymentsScreen />;
    case "admin-coupons":
      return <AdminCouponsScreen />;
    case "admin-reports":
      return <AdminReportsScreen />;
    case "admin-foods":
      return <AdminFoodsScreen />;
    case "admin-exercises":
      return <AdminExercisesScreen />;
    case "admin-admins":
      return <AdminAdminsScreen />;
    case "admin-audit-log":
      return <AdminAuditLogScreen />;
    case "admin-assets":
      return <AdminAssetsScreen />;
    case "admin-ai-usage":
      return <AdminAiUsageScreen />;
    case "terms":
      return <TermsScreen />;
    case "privacy":
      return <PrivacyScreen />;
    default:
      return <PlaceholderScreen route={route} />;
  }
}

// ============= SideRail =============

interface SideRailProps {
  user: { name: string; email: string; picture_url?: string | null; plan_tier: string; brand_letters?: string | null };
  route: Route;
  go: (r: Route, query?: string) => void;
  isAdmin: boolean;
  onLogout: () => void;
}

function SideRail({ user, route, go, isAdmin, onLogout }: SideRailProps) {
  const initials = (user.brand_letters || initialsFromName(user.name) || "AL").slice(0, 2).toUpperCase();
  const tierLabel = TIER_LABELS[user.plan_tier] || user.plan_tier;
  // El Avatar component requiere `initials` y opcional `avatar_color`. AetherLiteUser
  // no tiene avatar_color: usamos --primary via inline style.
  const avatarUser = { initials, avatar_color: "var(--primary)" };

  // SideRail tiene dos vistas TOTALMENTE separadas:
  //  - Admin (isAdmin === true): SOLO opciones de gestión Nilabs (entrenadores,
  //    pagos, reportes, plantillas globales, catálogos). El admin NO ve
  //    "clientes / biblioteca / mi marca / medidas" porque esas son funciones
  //    de coaching, no de plataforma.
  //  - Trainer (resto de usuarios): nav clásico de entrenador.
  // Como darknilton es admin pero también tiene cliente de testing (Angy),
  // dejamos un acceso discreto "Modo entrenador" al fondo del rail admin.
  return (
    <aside className={"side-rail" + (isAdmin ? " side-rail--admin" : "")}>
      <div className="brand">
        <div className="brand-mark">{isAdmin ? "NA" : "AL"}</div>
        <div className="brand-name">
          {isAdmin ? (
            <>Nilabs<span> Admin</span></>
          ) : (
            <>Aether<span>Lite</span></>
          )}
        </div>
      </div>

      {isAdmin ? (
        <>
          <div className="nav-section">
            <div className="nav-section-title">Plataforma</div>
            <NavItem icon="settings" label="Centro de control" active={route === "admin-dashboard"} onClick={() => go("admin-dashboard")} />
            <NavItem icon="user" label="Entrenadores" active={route === "admin-users"} onClick={() => go("admin-users")} />
            <NavItem icon="money" label="Pagos" active={route === "admin-payments"} onClick={() => go("admin-payments")} />
            <NavItem icon="gift" label="Cupones" active={route === "admin-coupons"} onClick={() => go("admin-coupons")} />
            <NavItem icon="sparkle" label="Reportes" active={route === "admin-reports"} onClick={() => go("admin-reports")} />
            <NavItem icon="sparkle" label="Uso IA" active={route === "admin-ai-usage"} onClick={() => go("admin-ai-usage")} />
          </div>

          <div className="nav-section">
            <div className="nav-section-title">Equipo Nilabs</div>
            <NavItem icon="user" label="Administradores" active={route === "admin-admins"} onClick={() => go("admin-admins")} />
            <NavItem icon="book" label="Registro de actividad" active={route === "admin-audit-log"} onClick={() => go("admin-audit-log")} />
          </div>

          <div className="nav-section">
            <div className="nav-section-title">Contenido global</div>
            <NavItem icon="grid" label="Plantillas base" active={route === "templates"} onClick={() => go("templates")} />
            <NavItem icon="hammer" label="Ejercicios" active={route === "admin-exercises"} onClick={() => go("admin-exercises")} />
            <NavItem icon="gift" label="Alimentos" active={route === "admin-foods"} onClick={() => go("admin-foods")} />
            <NavItem icon="sparkle" label="Assets visuales" active={route === "admin-assets"} onClick={() => go("admin-assets")} />
          </div>

          <div className="nav-section">
            <div className="nav-section-title">Cuenta</div>
            <NavItem icon="settings" label="Configuración" active={route === "config"} onClick={() => go("config")} />
          </div>

        </>
      ) : (
        <>
          <div className="nav-section">
            <div className="nav-section-title">Principal</div>
            <NavItem icon="home" label="Inicio" active={route === "dashboard"} onClick={() => go("dashboard")} />
            <NavItem icon="grid" label="Plantillas" active={route === "templates"} onClick={() => go("templates")} />
            <NavItem icon="user" label="Clientes" active={route === "clients"} onClick={() => go("clients")} />
            <NavItem icon="calendar" label="Agenda" active={route === "agenda"} onClick={() => go("agenda")} />
          </div>

          <div className="nav-section">
            <div className="nav-section-title">Herramientas</div>
            <NavItem icon="book" label="Biblioteca" active={route === "library"} onClick={() => go("library")} />
            <NavItem icon="sparkle" label="Medidas" active={route === "measurements"} onClick={() => go("measurements")} />
          </div>

          <div className="nav-section">
            <div className="nav-section-title">Cuenta</div>
            <NavItem icon="star" label="Mi marca" active={route === "brand"} onClick={() => go("brand")} />
            <NavItem icon="settings" label="Configuración" active={route === "config"} onClick={() => go("config")} />
          </div>
        </>
      )}

      <div style={{ flex: 1 }} />

      {isAdmin && (
        // Acceso discreto al modo trainer — solo visible para admins. Para
        // testing end-to-end del flujo de entrenador (darknilton tiene a Angy
        // como cliente real para verificar planes/links públicos).
        <button
          type="button"
          className="side-rail-to-trainer"
          onClick={() => go("dashboard")}
          title="Cambiar a vista de entrenador (testing con clientes reales)"
        >
          <Icon name="user" size={12} /> Modo entrenador (testing)
        </button>
      )}

      <div
        style={{ background: "var(--bg-subtle)", borderRadius: 12, padding: 12, display: "flex", gap: 10, alignItems: "center", cursor: "pointer" }}
        onClick={() => go("config")}
        title="Mi cuenta"
      >
        {user.picture_url ? (
          <img src={user.picture_url} alt="" width={36} height={36} style={{ borderRadius: "50%", objectFit: "cover" }} />
        ) : (
          <Avatar user={avatarUser as any} />
        )}
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user.name || user.email}</div>
          <div className="tiny muted">{tierLabel}</div>
        </div>
        <button
          className="icon-btn"
          style={{ width: 32, height: 32 }}
          onClick={(e) => {
            e.stopPropagation();
            onLogout();
          }}
          title="Cerrar sesión"
        >
          <Icon name="logout" size={14} />
        </button>
      </div>
    </aside>
  );
}

const TIER_LABELS: Record<string, string> = {
  free: "Plan Free",
  pro: "Plan Pro",
  studio: "Plan Studio",
};

function initialsFromName(name: string): string {
  if (!name) return "";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2);
  return (parts[0][0] + parts[parts.length - 1][0]);
}

// ============= NavItem =============

interface NavItemProps {
  icon: string;
  label: string;
  active: boolean;
  onClick: () => void;
  badge?: number;
  accent?: "amber" | "magenta";
}

function NavItem({ icon, label, active, onClick, badge, accent }: NavItemProps) {
  return (
    <button className={"nav-item " + (active ? "active" : "")} onClick={onClick}>
      <span className="icon">
        <Icon name={icon} size={18} />
      </span>
      <span>{label}</span>
      {badge ? (
        <span
          className="badge-count"
          style={accent === "amber" ? ({ background: "var(--accent-amber)", color: "#4a2900" } as CSSProperties) : undefined}
        >
          {badge}
        </span>
      ) : null}
    </button>
  );
}

// ============= TopBar =============

interface TopBarProps {
  route: Route;
  go: (r: Route, query?: string) => void;
  user: { name: string; email: string; picture_url?: string | null; brand_letters?: string | null };
  onToggleMobileMenu: () => void;
}

function TopBar({ route, user, onToggleMobileMenu }: TopBarProps) {
  const initials = (user.brand_letters || initialsFromName(user.name) || "AL").slice(0, 2).toUpperCase();
  return (
    <header className="topbar">
      <div className="page-title">{ROUTE_TITLES[route] || "AetherLite"}</div>
      <div className="spacer" />
      <div className="search-box hide-mobile">
        <Icon name="search" size={16} />
        <input placeholder="Buscar plantillas, ejercicios, clientes…" />
      </div>
      <button
        className="icon-btn"
        style={{ background: "transparent", border: 0, padding: 0 }}
        onClick={onToggleMobileMenu}
        aria-label="Menú"
        title="Menú / cuenta"
      >
        {user.picture_url ? (
          <img src={user.picture_url} alt="" width={36} height={36} style={{ borderRadius: "50%", objectFit: "cover" }} />
        ) : (
          <Avatar user={{ initials, avatar_color: "var(--primary)" } as any} />
        )}
      </button>
    </header>
  );
}

// ============= MobileMenu =============

interface MobileMenuProps {
  route: Route;
  go: (r: Route, query?: string) => void;
  isAdmin: boolean;
  onLogout: () => void;
}

function MobileMenu({ go, isAdmin, onLogout }: MobileMenuProps) {
  return (
    <div className="user-menu-mobile" onClick={(e) => e.stopPropagation()}>
      <button className="nav-item" onClick={() => go("dashboard")}>
        <span className="icon"><Icon name="home" size={16} /></span> Inicio
      </button>
      <button className="nav-item" onClick={() => go("templates")}>
        <span className="icon"><Icon name="grid" size={16} /></span> Plantillas
      </button>
      <button className="nav-item" onClick={() => go("clients")}>
        <span className="icon"><Icon name="user" size={16} /></span> Clientes
      </button>
      <button className="nav-item" onClick={() => go("library")}>
        <span className="icon"><Icon name="book" size={16} /></span> Biblioteca
      </button>
      <button className="nav-item" onClick={() => go("measurements")}>
        <span className="icon"><Icon name="sparkle" size={16} /></span> Medidas
      </button>

      <div style={{ borderTop: "1px solid var(--border)", margin: "8px 0" }} />
      <button className="nav-item" onClick={() => go("brand")}>
        <span className="icon"><Icon name="star" size={16} /></span> Mi marca
      </button>
      <button className="nav-item" onClick={() => go("config")}>
        <span className="icon"><Icon name="settings" size={16} /></span> Configuración
      </button>

      {isAdmin && (
        <>
          <div style={{ borderTop: "1px solid var(--border)", margin: "8px 0" }} />
          <button className="nav-item" onClick={() => go("admin-dashboard")}>
            <span className="icon"><Icon name="settings" size={16} /></span> Centro de control
          </button>
        </>
      )}

      <div style={{ borderTop: "1px solid var(--border)", margin: "8px 0" }} />
      <button className="nav-item" onClick={onLogout}>
        <span className="icon"><Icon name="logout" size={16} /></span> Cerrar sesión
      </button>
    </div>
  );
}

// ============= BottomNav =============

interface BottomNavProps {
  route: Route;
  go: (r: Route, query?: string) => void;
}

function BottomNav({ route, go }: BottomNavProps) {
  const items: { r: Route; icon: string; label: string }[] = [
    { r: "dashboard", icon: "home", label: "Inicio" },
    { r: "templates", icon: "grid", label: "Plantillas" },
    { r: "library", icon: "book", label: "Biblioteca" },
    { r: "measurements", icon: "sparkle", label: "Medidas" },
    { r: "brand", icon: "star", label: "Marca" },
  ];
  return (
    <nav className="bottom-nav" aria-label="Navegación principal">
      {items.map((it) => {
        const active = route === it.r;
        return (
          <button
            key={it.r}
            className={"bottom-nav-item " + (active ? "active" : "")}
            onClick={() => go(it.r)}
          >
            <span className="icon-wrap"><Icon name={it.icon} size={20} /></span>
            <span>{it.label}</span>
          </button>
        );
      })}
    </nav>
  );
}

// ============= Footer =============

function Footer({ go }: { go: (r: Route, query?: string) => void }) {
  return (
    <footer className="app-footer">
      <div className="app-footer-left">
        <span style={{ fontWeight: 700, fontFamily: "var(--font-display)" }}>AetherLite</span>
        <span className="tiny muted"> · una iniciativa de </span>
        <a href="https://nilabs.co" target="_blank" rel="noopener noreferrer" className="footer-link">Nilabs</a>
      </div>
      <div className="app-footer-right">
        <a onClick={() => go("terms")} className="footer-link">Términos</a>
        <a onClick={() => go("privacy")} className="footer-link">Privacidad</a>
        <a href="mailto:contacto@nilabs.co" className="footer-link">contacto@nilabs.co</a>
      </div>
    </footer>
  );
}

// ============= TweaksPanel =============

interface TweakProps {
  tweaks: Tweaks;
  setTweak: (key: keyof Tweaks | Partial<Tweaks>, value?: Tweaks[keyof Tweaks]) => void;
}

function TweaksPanelMounted({ tweaks, setTweak }: TweakProps) {
  return (
    <TweaksPanel title="Tweaks">
      <TweakSection label="Apariencia" />
      <TweakRadio
        label="Tema"
        value={tweaks.theme}
        onChange={(v) => setTweak("theme", v as Tweaks["theme"])}
        options={[
          { value: "light", label: "Claro" },
          { value: "dark", label: "Oscuro" },
        ]}
      />
      <TweakRadio
        label="Densidad"
        value={tweaks.density}
        onChange={(v) => setTweak("density", v as Tweaks["density"])}
        options={[
          { value: "comfortable", label: "Cómoda" },
          { value: "compact", label: "Compacta" },
        ]}
      />

      <TweakSection label="Paleta" />
      <TweakColor label="Primario" value={tweaks.primaryColor} onChange={(v) => setTweak("primaryColor", v)} />
      <TweakColor label="Acento" value={tweaks.accentColor} onChange={(v) => setTweak("accentColor", v)} />
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
        {[
          { p: "#7c3aed", a: "#ec4899", name: "Violeta AL" },
          { p: "#059669", a: "#ec4899", name: "Cancha" },
          { p: "#2563eb", a: "#f59e0b", name: "Cobalto" },
          { p: "#dc2626", a: "#1d4ed8", name: "Clásico" },
          { p: "#0891b2", a: "#f97316", name: "Cyan" },
        ].map((p) => (
          <button
            key={p.name}
            onClick={() => setTweak({ primaryColor: p.p, accentColor: p.a })}
            style={{
              padding: "6px 10px",
              borderRadius: 8,
              border: "1px solid var(--border)",
              background: "var(--bg-elev)",
              fontSize: 12,
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: 6,
              cursor: "pointer",
            }}
          >
            <span style={{ width: 12, height: 12, borderRadius: 3, background: p.p, display: "inline-block" }} />
            <span style={{ width: 12, height: 12, borderRadius: 3, background: p.a, display: "inline-block" }} />
            {p.name}
          </button>
        ))}
      </div>

      <TweakSection label="Tipografía" />
      <TweakSelect
        label="Display"
        value={tweaks.fontDisplay}
        onChange={(v) => setTweak("fontDisplay", v)}
        options={[
          { value: "Bricolage Grotesque", label: "Bricolage Grotesque" },
          { value: "Archivo", label: "Archivo" },
          { value: "Space Grotesk", label: "Space Grotesk" },
          { value: "DM Serif Display", label: "DM Serif Display" },
          { value: "Anton", label: "Anton (deportivo)" },
        ]}
      />
      <TweakSelect
        label="Cuerpo"
        value={tweaks.fontBody}
        onChange={(v) => setTweak("fontBody", v)}
        options={[
          { value: "Inter Tight", label: "Inter Tight" },
          { value: "DM Sans", label: "DM Sans" },
          { value: "Manrope", label: "Manrope" },
          { value: "IBM Plex Sans", label: "IBM Plex Sans" },
        ]}
      />
    </TweaksPanel>
  );
}
